/**
 * Reminder plans: a short series of scheduled broadcasts (an RSVP chase, a
 * week-out note, a day-before, a morning-of) created together and shown and
 * cancelled as one thing — the Studio side of OrchestratorAPI #394.
 *
 * Every rule that can be got wrong is a pure function here, tested in
 * `reminderPlan.test.ts`; the dialog and the list are thin wiring over it, and
 * their behaviour is proved by the render tests against a mock of the API.
 *
 * Four rules this module exists to keep:
 *
 * 1. TRIP-LOCAL times. "10:00" means 10:00 where the trip is, on the day named
 *    by `dateStart`, converted with `wallClockToIso` at THAT instant. Adding
 *    `7 * 24h` to a timestamp instead lands an hour off across a DST change.
 * 2. DISTINCT copy per step. SuprSend silently dedupes IDENTICAL copy to the
 *    same guest within 24h, so a day-before and a morning-of with the same
 *    words would arrive as one message and the guest would never know the
 *    second one existed. Every step names its purpose AND the trip date, and
 *    {@link planIssues} refuses a plan whose copy repeats.
 * 3. The audience is a KIND, never a list. Plan rows carry `audienceKind`
 *    (decided by the server when the row fires) and no `inviteeIds`/`segment`.
 * 4. A plan is ONE atomic call to the API (every step or none), and a repeat of
 *    it — same plan id, same steps — is answered from what exists, so a retry can
 *    never make a second plan. {@link planBodyOf} builds that call.
 */
import { EMAIL, SMS, normalizeChannels, type BroadcastChannel } from './broadcastChannels';
import { audienceKindLabel, isReminderSequencesUnavailable, type AudienceKind } from './audienceKind';
import { buildScheduleRequest, isScheduledBroadcastsUnavailable, sendAtIsInFuture } from './scheduledBroadcasts';
import { toSmsSafe } from './smsSafeText';
import { isoToWallClock, wallClockToIso } from './tripTime';
import { AUDIENCE_ALL } from '../components/groups/broadcasts/audience';
import { mutationErrorMessage } from './mutation-error';
import { entryLine, readPlanErrorEntries } from './reminderPlanErrors';
import type {
  CreateReminderPlanBody,
  ExcludedPlanStep,
  GroupInvitee,
  GroupScheduledBroadcast,
  ReminderPlanStepBody,
  ScheduleGroupBroadcastRequest,
  ScheduledSequenceCancelResult,
} from '../types/groups';

export type PlanStepKey = 'rsvp_chase' | 'week_out' | 'day_before' | 'morning_of';
/**
 * What goes on the wire as `sequenceStep`: ALWAYS the preset key. The API identifies a step
 * by (group, sequenceId, sequenceStep), so the label is the step's identity and must never
 * depend on anything that can change between a create and its retry. "Moved off its default
 * day" is worked out from `sendAt`, never encoded in the label. (A label this client did not
 * mint — a row from an older Studio — is kept as it is when a plan is rescheduled.)
 */
export type SequenceStepName = string;

export interface StepPreset {
  key: PlanStepKey;
  label: string;
  audienceKind: AudienceKind;
  /** Whole days before `dateStart` (0 = the day itself). */
  daysBefore: number;
  /** Trip-local wall clock, HH:mm. */
  time: string;
}

export const STEP_PRESETS: readonly StepPreset[] = [
  { key: 'rsvp_chase', label: 'RSVP chase', audienceKind: 'rsvp_no_reply', daysBefore: 14, time: '10:00' },
  { key: 'week_out', label: 'Week out', audienceKind: 'rsvp_going', daysBefore: 7, time: '10:00' },
  { key: 'day_before', label: 'Day before', audienceKind: 'rsvp_going', daysBefore: 1, time: '17:00' },
  { key: 'morning_of', label: 'Morning of', audienceKind: 'rsvp_going', daysBefore: 0, time: '08:30' },
];

const PRESET_BY_KEY = new Map(STEP_PRESETS.map((preset) => [preset.key as string, preset]));

export const SMS_MAX = 280;
export const SUBJECT_MAX = 200;
export const BODY_MAX = 5000;

// ---- calendar arithmetic on YYYY-MM-DD (no time zones involved, by design) ----

const YMD = /^(\d{4})-(\d{2})-(\d{2})/;

/** `YYYY-MM-DD` from a trip date, or null. Accepts a full ISO string and keeps only the day. */
export function normalizeTripDate(value: string | null | undefined): string | null {
  const match = YMD.exec((value ?? '').trim());
  if (!match) return null;
  const [, y, m, d] = match;
  const probe = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  // Reject 2026-02-31 style dates that Date.UTC would quietly roll over.
  if (probe.getUTCFullYear() !== Number(y) || probe.getUTCMonth() !== Number(m) - 1 || probe.getUTCDate() !== Number(d)) {
    return null;
  }
  return `${y}-${m}-${d}`;
}

function ymdToUtcMs(ymd: string): number {
  const match = YMD.exec(ymd)!;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** Calendar-day arithmetic: `addDaysYmd('2026-10-25', -7)` is `2026-10-18`. */
export function addDaysYmd(ymd: string, days: number): string {
  const date = new Date(ymdToUtcMs(ymd) + days * 86_400_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

/** Whole calendar days from `from` to `to` (positive when `to` is later). */
export function daysBetweenYmd(from: string, to: string): number {
  return Math.round((ymdToUtcMs(to) - ymdToUtcMs(from)) / 86_400_000);
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Fri Oct 16" — locale-free, so a copy draft reads the same for every host. */
export function tripDateLabel(ymd: string): string {
  const date = new Date(ymdToUtcMs(ymd));
  return `${WEEKDAYS[date.getUTCDay()]} ${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`;
}

// ---- prerequisites and defaults ----

export type PlanPrerequisite =
  | { ok: true; dateStart: string; timeZone: string }
  | { ok: false; reason: string };

/**
 * A plan is anchored to `dateStart` in the trip's own zone. Without a real zone
 * (not the browser fallback that the single-send scheduler tolerates) "10:00"
 * would silently mean the host's 10:00, so refuse and say what to fix.
 */
export function planPrerequisite(
  dateStart: string | null | undefined,
  timeZone: string | null | undefined,
  zoneIsFallback: boolean,
): PlanPrerequisite {
  const start = normalizeTripDate(dateStart);
  const zone = (timeZone ?? '').trim();
  const missing: string[] = [];
  if (!start) missing.push('a start date');
  if (!zone || zoneIsFallback) missing.push('a time zone');
  if (missing.length > 0 || !start) {
    return { ok: false, reason: `Reminder plans are timed against the trip, so this trip needs ${missing.join(' and ')} first.` };
  }
  return { ok: true, dateStart: start, timeZone: zone };
}

/** Email + SMS when anyone on the roster has a phone number, otherwise email only. */
export function defaultPlanChannels(invitees: ReadonlyArray<Pick<GroupInvitee, 'phone'>> | null | undefined): BroadcastChannel[] {
  const hasPhones = (invitees ?? []).some((invitee) => Boolean(invitee.phone?.trim()));
  return hasPhones ? [EMAIL, SMS] : [EMAIL];
}

export interface PlanStepDraft {
  key: PlanStepKey;
  enabled: boolean;
  /** Trip-local `YYYY-MM-DDTHH:mm`, exactly what a datetime-local input holds. */
  when: string;
  subject: string;
  body: string;
  smsBody: string;
}

/** The default trip-local send time of a preset for a given trip date. */
export function defaultStepWhen(preset: StepPreset, dateStart: string): string {
  return `${addDaysYmd(dateStart, -preset.daysBefore)}T${preset.time}`;
}

/** True unless `when` (trip-local) is a real instant strictly after `nowMs`. */
export function stepIsPast(when: string, timeZone: string, nowMs: number = Date.now()): boolean {
  return !sendAtIsInFuture(wallClockToIso(when, timeZone), nowMs);
}

// ---- copy ----

const clip = (text: string, max: number) => (text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text);
/** ASCII-only clip for SMS: a "…" would be the one non-GSM-7 character in an otherwise safe text. */
const clipSms = (text: string, max: number) => (text.length > max ? `${text.slice(0, max - 3).trimEnd()}...` : text);

/**
 * Drafted copy for one step. Distinct BY CONSTRUCTION: each step's subject and
 * SMS open with its own purpose ("RSVP please", "One week to go", "Tomorrow",
 * "Today") and carry the trip date, so SuprSend's 24h dedupe cannot merge two
 * steps. The SMS is plain GSM-7 (via `toSmsSafe`), at most 280 characters, and
 * has NO `{url}` — the concierge appends its own heading and the trip link.
 * Email bodies use `{name}` and `{url}`, which the broadcast API fills per guest.
 */
export function stepCopy(
  key: PlanStepKey,
  trip: { title: string; dateStart: string },
): { subject: string; body: string; smsBody: string } {
  const title = clip(trip.title.trim() || 'the trip', 80);
  const date = tripDateLabel(trip.dateStart);
  const at = `${title}, ${date}`;
  let subject: string;
  let body: string;
  let sms: string;
  switch (key) {
    case 'rsvp_chase':
      subject = `RSVP please: ${at}`;
      body = `Hi {name},\n\nWe haven't heard from you yet about ${title} (${date}). Could you let us know whether you can make it? It only takes a moment.\n\nRSVP and see the details here: {url}`;
      sms = `RSVP please: we haven't heard from you yet about ${at}. Can you make it? Tap the link to answer.`;
      break;
    case 'week_out':
      subject = `One week to go: ${at}`;
      body = `Hi {name},\n\n${title} is one week away (${date}). If you haven't yet, now is a good time to book your travel and read through the schedule for anything you need to plan around.\n\nEverything is on the trip page: {url}`;
      sms = `One week to go: ${at}. Time to sort your travel and check the schedule on the trip page.`;
      break;
    case 'day_before':
      subject = `Tomorrow: ${at}`;
      body = `Hi {name},\n\n${title} starts tomorrow (${date}). Here is the schedule and the details you will want handy: {url}\n\nSafe travels, and see you soon.`;
      sms = `Tomorrow: ${at}. The schedule and details are on the trip page. Safe travels!`;
      break;
    case 'morning_of':
    default:
      subject = `Today: ${at}`;
      body = `Hi {name},\n\nToday is the day: ${title} (${date}). Today's schedule and details are here: {url}\n\nSee you there.`;
      sms = `Today: ${at}. Today's schedule is on the trip page. See you there!`;
      break;
  }
  return {
    subject: clip(subject, SUBJECT_MAX),
    body: clip(body, BODY_MAX),
    smsBody: clipSms(toSmsSafe(sms).text, SMS_MAX),
  };
}

/** The four preset steps, with copy, at their default trip-local times. Past steps come back unticked. */
export function buildPlanDrafts(input: {
  title: string;
  dateStart: string;
  timeZone: string;
  nowMs?: number;
}): PlanStepDraft[] {
  const nowMs = input.nowMs ?? Date.now();
  return STEP_PRESETS.map((preset) => {
    const when = defaultStepWhen(preset, input.dateStart);
    return {
      key: preset.key,
      enabled: !stepIsPast(when, input.timeZone, nowMs),
      when,
      ...stepCopy(preset.key, { title: input.title, dateStart: input.dateStart }),
    };
  });
}

/**
 * Move a step to a new trip-local time. A step the host drags from the past to
 * the future is switched on (that is what editing a past step's date is for —
 * the "custom chase date" case); one dragged into the past is switched off.
 */
export function setStepWhen(
  draft: PlanStepDraft,
  when: string,
  timeZone: string,
  nowMs: number = Date.now(),
): PlanStepDraft {
  const wasPast = stepIsPast(draft.when, timeZone, nowMs);
  const isPast = stepIsPast(when, timeZone, nowMs);
  return { ...draft, when, enabled: isPast ? false : wasPast ? true : draft.enabled };
}

// ---- validation ----

const fingerprint = (text: string) => text.replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * Pairs of enabled steps whose copy is the same. Any one of subject, email body
 * or (when SMS is picked) text repeating is enough: SuprSend dedupes per
 * channel, so a repeated text alone silently drops a guest's second reminder.
 */
export function duplicateCopy(
  drafts: readonly PlanStepDraft[],
  channels: readonly string[],
): Array<{ a: PlanStepKey; b: PlanStepKey; field: 'subject' | 'body' | 'sms' }> {
  const active = drafts.filter((draft) => draft.enabled);
  const smsOn = normalizeChannels(channels).includes(SMS);
  const found: Array<{ a: PlanStepKey; b: PlanStepKey; field: 'subject' | 'body' | 'sms' }> = [];
  for (let i = 0; i < active.length; i += 1) {
    for (let j = i + 1; j < active.length; j += 1) {
      const a = active[i];
      const b = active[j];
      if (fingerprint(a.subject) === fingerprint(b.subject)) found.push({ a: a.key, b: b.key, field: 'subject' });
      if (fingerprint(a.body) === fingerprint(b.body)) found.push({ a: a.key, b: b.key, field: 'body' });
      if (smsOn && fingerprint(a.smsBody) === fingerprint(b.smsBody)) found.push({ a: a.key, b: b.key, field: 'sms' });
    }
  }
  return found;
}

const labelOf = (key: string) => PRESET_BY_KEY.get(key)?.label ?? key;

/** Everything that stops a plan from being created, as sentences a host can act on. Empty = good to go. */
export function planIssues(
  drafts: readonly PlanStepDraft[],
  channels: readonly string[],
  timeZone: string,
  nowMs: number = Date.now(),
): string[] {
  const issues: string[] = [];
  const active = drafts.filter((draft) => draft.enabled);
  if (active.length === 0) return ['Turn on at least one reminder.'];
  const smsOn = normalizeChannels(channels).includes(SMS);
  for (const draft of active) {
    const name = labelOf(draft.key);
    if (stepIsPast(draft.when, timeZone, nowMs)) issues.push(`${name}: pick a time in the future.`);
    if (!draft.subject.trim()) issues.push(`${name}: the subject is empty.`);
    if (draft.subject.length > SUBJECT_MAX) issues.push(`${name}: the subject is over ${SUBJECT_MAX} characters.`);
    if (!draft.body.trim()) issues.push(`${name}: the email message is empty.`);
    if (draft.body.length > BODY_MAX) issues.push(`${name}: the email message is over ${BODY_MAX} characters.`);
    if (smsOn) {
      const sms = toSmsSafe(draft.smsBody).text;
      if (!sms.trim()) issues.push(`${name}: write the text message, or untick SMS.`);
      if (sms.length > SMS_MAX) issues.push(`${name}: the text message is over ${SMS_MAX} characters.`);
    }
  }
  for (const dup of duplicateCopy(drafts, channels)) {
    const field = dup.field === 'sms' ? 'text message' : dup.field === 'body' ? 'email message' : 'subject';
    issues.push(
      `${labelOf(dup.a)} and ${labelOf(dup.b)} have the same ${field}. Guests are only sent identical wording once a day, so make each reminder say what it is for.`,
    );
  }
  return issues;
}

// ---- payloads ----

export interface PlanRequest {
  /** Set by `planReschedule`: this step would be sent AFTER the new trip start day, so the confirm holds it back until acknowledged. */
  lateAfterStart?: boolean;
  step: SequenceStepName;
  /** Human name of the step, for confirm and progress text. */
  label: string;
  audienceKind: string | null;
  sendAt: string;
  /** Reschedule only: the old row was already attempted, so the API cancels it instead of recreating it (see `ReschedulePlan.inDoubt`). */
  inDoubt?: boolean;
  request: ScheduleGroupBroadcastRequest;
}

/**
 * One schedule request per enabled step, soonest first, all carrying the SAME
 * `sequenceId`. The audience is `audienceKind` ALONE — no `inviteeIds`, no
 * `segment` (the API 400s on the combination; `buildScheduleRequest` enforces
 * it in one place). SMS text goes through `toSmsSafe` again on the way out so a
 * host's edit cannot turn a one-segment text into a doubled bill.
 */
export function buildPlanRequests(input: {
  drafts: readonly PlanStepDraft[];
  channels: readonly string[];
  timeZone: string;
  dateStart: string;
  personalizeInviteLinks: boolean;
  sequenceId: string;
  /** How many steps the plan has. Defaults to the number of requests built here. */
  totalSteps?: number;
}): PlanRequest[] {
  const channels = normalizeChannels(input.channels);
  const smsSelected = channels.includes(SMS);
  const requests: PlanRequest[] = [];
  for (const draft of input.drafts) {
    if (!draft.enabled) continue;
    const preset = PRESET_BY_KEY.get(draft.key);
    const sendAt = wallClockToIso(draft.when, input.timeZone);
    if (!preset || !sendAt) continue;
    const step = draft.key;
    requests.push({
      step,
      label: preset.label,
      audienceKind: preset.audienceKind,
      sendAt,
      request: buildScheduleRequest({
        subject: draft.subject.trim(),
        body: draft.body,
        channels,
        smsSelected,
        smsBody: toSmsSafe(draft.smsBody).text,
        segment: AUDIENCE_ALL,
        audienceKind: preset.audienceKind,
        personalizeInviteLinks: input.personalizeInviteLinks,
        sendAt,
        sequenceId: input.sequenceId,
        sequenceStep: step,
        sequenceTotalSteps: 0, // set below, once the number of steps is known
      }),
    });
  }
  const totalSteps = input.totalSteps ?? requests.length;
  for (const entry of requests) entry.request.sequenceTotalSteps = totalSteps;
  return requests.sort((a, b) => Date.parse(a.sendAt) - Date.parse(b.sendAt));
}

/** A new plan id. `crypto.randomUUID` exists in every browser Studio supports; the fallback is for old test runtimes. */
export function newSequenceId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = Math.floor(Math.random() * 16);
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** One step's wire body for the atomic plan endpoints (the plan-level fields go beside the steps, not in each). */
export function stepBodyOf(request: ScheduleGroupBroadcastRequest): ReminderPlanStepBody {
  return {
    sequenceStep: request.sequenceStep ?? '',
    subject: request.subject,
    body: request.body,
    smsBody: request.smsBody ?? null,
    channels: request.channels ?? [],
    ...(request.audienceKind ? { audienceKind: request.audienceKind } : { segment: request.segment ?? null, ...(request.inviteeIds ? { inviteeIds: request.inviteeIds } : {}) }),
    personalizeInviteLinks: Boolean(request.personalizeInviteLinks),
    sendAt: request.sendAt,
  };
}

/** The body of `POST .../plans`: the whole plan under one id, built from the trip start date `anchorDate`. Every step carries its preset key. */
export function planBodyOf(sequenceId: string, anchorDate: string, requests: readonly PlanRequest[]): CreateReminderPlanBody {
  return { sequenceId, anchorDate, steps: requests.map((entry) => stepBodyOf(entry.request)) };
}

/**
 * Did the request possibly reach the server and save a row even though we got
 * no usable answer? A timeout, a dropped connection (axios: an error with no
 * `response`), a 408 and ANY 5xx (a 500 can land after the INSERT committed; a
 * 502/504 is a gateway giving up on a request the server may still finish) say
 * nothing about whether the row exists. The DESIGNED 503s (migration 014/016
 * not applied) and every 4xx are real answers: nothing was saved.
 */
export function isUnknownOutcomeError(error: unknown): boolean {
  if (!error) return false;
  const response = (error as { response?: { status?: number } }).response;
  if (!response) {
    // Transport-level: axios marks these (`isAxiosError`, `code`, `request`).
    const e = error as { isAxiosError?: boolean; code?: string; request?: unknown };
    return Boolean(e.isAxiosError || e.code || e.request);
  }
  if (isReminderSequencesUnavailable(error) || isScheduledBroadcastsUnavailable(error)) return false;
  return response.status === 408 || (response.status ?? 0) >= 500;
}

/** What a failed plan call means for the host. Only `unknown` leaves the plan possibly saved. */
export type PlanErrorKind =
  | 'unavailable' | 'invalid' | 'conflict' | 'cancelled' | 'nothing_to_reschedule' | 'plan_changed' | 'unlabelled_steps' | 'unknown' | 'other';

/** Classify an error from the plan endpoints (the API's stable `code`s, or the HTTP status). */
export function classifyPlanError(error: unknown): PlanErrorKind {
  if (isReminderSequencesUnavailable(error) || isScheduledBroadcastsUnavailable(error)) return 'unavailable';
  const response = (error as { response?: { status?: number; data?: { code?: string } } })?.response;
  // An API that has no plan endpoint yet answers 404/405: the same "not available here" state.
  if (response?.status === 404 || response?.status === 405) return 'unavailable';
  if (isUnknownOutcomeError(error)) return 'unknown';
  const code = response?.data?.code;
  if (response?.status === 409 && code === 'nothing_to_reschedule') return 'nothing_to_reschedule';
  // A Reschedule the server refused WITHOUT changing anything: a reminder it expected to leave out went back to waiting while it ran
  // (`plan_changed`), or the old plan has live reminders with no step label (`plan_unlabelled_steps`). Neither is "already rescheduled".
  if (response?.status === 409 && code === 'plan_changed') return 'plan_changed';
  if (response?.status === 409 && code === 'plan_unlabelled_steps') return 'unlabelled_steps';
  if (response?.status === 409 && code === 'sequence_cancelled') return 'cancelled';
  if (response?.status === 409) return 'conflict';
  if (response?.status === 400) return 'invalid';
  return 'other';
}

/**
 * Does this error SETTLE an unresolved create attempt, i.e. prove what exists under its id?
 *
 * Only a definitive answer about the id may forget an attempt: a 2xx (not an error), a 409 `plan_conflict` / `sequence_cancelled`
 * (the id exists, or was closed or discarded), and the designed "not available here" answers (404/405, a 503 from a server that
 * has no plan tables). For an attempt whose outcome was UNKNOWN (a timeout or 5xx on a call that may have committed — `afterUnknownOutcome`),
 * what a RETRY answers is not enough: the earlier request may still be committing (a 400 from the retry's own validation, or a 503
 * from the 018 latch — the API keeps answering "not available" for up to a minute after the migration — say nothing about it). Such an attempt
 * is settled only by `/abandon` (see `discardAttempt`: the server waits for the plan lock, then reports rows or records a tombstone) or by its
 * rows appearing on the list.
 *
 * A first call's 400 that names a step (`steps[i]: …`) is rejected before anything is written, so it settles. A 400 naming no step, and a
 * 401 / 403 / 429 / other failure, say nothing about the id: the attempt stays remembered.
 */
export function settlesPlanAttempt(error: unknown, options: { afterUnknownOutcome?: boolean } = {}): boolean {
  const kind = classifyPlanError(error);
  if (kind === 'conflict' || kind === 'cancelled') return true;
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (kind === 'unavailable') return !(options.afterUnknownOutcome && status === 503);
  if (kind !== 'invalid' || options.afterUnknownOutcome) return false;
  return namesAStep(error);
}

/** Does a 400 name the failing step (`stepIndex`, or a message that starts `steps[i]`)? Only then was it rejected before anything was written. */
export function namesAStep(error: unknown): boolean {
  const data = (error as { response?: { data?: { stepIndex?: unknown; error?: unknown } } })?.response?.data;
  return typeof data?.stepIndex === 'number' || /^steps\[\d+\]/.test(String(data?.error ?? ''));
}

const AFTER_UNKNOWN = Symbol.for('studio.reminderPlan.afterUnknownOutcome');

/** Tag an error that came from a call made AFTER an earlier call of the same operation had an unknown outcome. */
export function markAfterUnknownOutcome<T>(error: T): T {
  if (error && typeof error === 'object') (error as Record<symbol, unknown>)[AFTER_UNKNOWN] = true;
  return error;
}

export function isAfterUnknownOutcome(error: unknown): boolean {
  return Boolean(error && typeof error === 'object' && (error as Record<symbol, unknown>)[AFTER_UNKNOWN]);
}

/**
 * Why an unresolved create attempt is still unresolved, in the host's words (shown beside Retry / Discard, so a disabled Create is never
 * unexplained). Null for an unknown-outcome error: the generic text already says it.
 */
export function unresolvedAttemptNote(error: unknown, afterUnknownOutcome: boolean): string | null {
  const kind = classifyPlanError(error);
  if (kind === 'unknown') return null;
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (kind === 'invalid') {
    const message = mutationErrorMessage(error);
    const quoted = message ? ` (“${message}”)` : '';
    return afterUnknownOutcome
      ? `The server refused the retry${quoted}, but the earlier request may still have been saved, so this attempt is still open.`
      : `The server refused the request${quoted} without naming a reminder, so we can't tell whether anything was saved.`;
  }
  if (kind === 'unavailable' && status === 503) {
    return "The server answered that reminder plans aren't available. Right after an update that can be a stale answer, and the earlier request may still have been saved.";
  }
  return `The server answered${status ? ` (${status})` : ''} without telling us whether the plan was saved.`;
}

/** The name a host knows a step by: its preset label, else the key. */
export function stepDisplayName(key: string | null | undefined): string {
  return (key && PRESET_BY_KEY.get(key)?.label) || key || 'a reminder';
}

/**
 * A sentence for the host. A 400's own text names the failing step by index (`steps[2]: sendAt must be in the future.`); given
 * the steps' labels in request order it is shown by NAME ("Day before: sendAt must be in the future.").
 */
export function describePlanError(error: unknown, stepLabels: readonly string[] = []): string {
  switch (classifyPlanError(error)) {
    case 'unavailable':
      return "Reminder plans aren't available yet on this server.";
    case 'unknown':
      return "We couldn't confirm whether the plan was saved. Nothing will be duplicated if you try again.";
    case 'cancelled':
      return 'That plan was cancelled or discarded, so it can no longer be added to. Check the Scheduled sends list.';
    case 'conflict':
      return 'A plan with that id already exists with different reminders. Check the Scheduled sends list.';
    case 'plan_changed':
      return 'The plan changed while rescheduling, so nothing was changed. The list has been refreshed — check it and try again.';
    case 'unlabelled_steps':
      return "Some reminders in this plan were made before reminders had step labels, so Reschedule can't tell whether they were already sent. Nothing was changed: cancel those reminders one by one first, then reschedule. A reminder that is sending right now can't be cancelled: wait for it to finish.";
    case 'nothing_to_reschedule': {
      // The server's own text is the accurate one: it says whether steps remain pending ("…the plan's other pending steps were not part of
      // the request…") or the plan has nothing left. Only an answer with no text falls back to the generic sentence.
      const text = (error as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
      const excluded = describeExcluded(excludedOf(error));
      const base = typeof text === 'string' && text.trim() ? text.trim() : 'This plan was already rescheduled, sent or cancelled — nothing was changed.';
      return excluded ? `${base} ${excluded}` : base;
    }
    default: {
      // The API answers `errors: [{ label, message }]` with EVERY failing step; list them all. `error` (the first) is only the fallback.
      const entries = readPlanErrorEntries(error);
      if (entries.length > 0) {
        const lines = entries.map((entry) => entryLine(entry, stepLabels, stepDisplayName));
        return lines.length === 1 ? lines[0] : `These reminders need a change:\n${lines.map((line) => `• ${line}`).join('\n')}`;
      }
      const message = mutationErrorMessage(error) || 'The server did not accept the plan.';
      const named = /^steps\[(\d+)\]:\s*(.*)$/s.exec(message);
      if (!named) return message;
      const label = stepLabels[Number(named[1])];
      return label ? `${stepDisplayName(label)}: ${named[2]}` : message;
    }
  }
}

/**
 * What the API writes to `cancelledBy` when Reschedule cancels a reminder whose outcome is unknown (an expired lease, or a send that was attempted):
 * distinct from the host's own cancel, so after a reload the row can still say it MAY have been sent.
 */
export const IN_DOUBT_CANCELLED_BY = 'reschedule:in_doubt';

/** How to recover from a reminder that may have been sent (and cancelled): "Reuse" on the row opens the Broadcast composer prefilled; the host re-sends it by hand. */
export const RESEND_HINT = "Check with a guest; if they didn't get it, use Reuse to send it again. Keep the wording unchanged: guests who already got it won't be sent it twice (duplicates are suppressed for 24 hours).";

/** The sentence under a cancelled row that was cancelled because it MAY have gone out. */
export const IN_DOUBT_ROW_NOTE = `May have been sent before it was cancelled. ${RESEND_HINT}`;

/** Was this row cancelled after it may have gone out (the API's in-doubt marker, or cancelled after an attempt)? */
export const isInDoubtCancel = (row: Pick<GroupScheduledBroadcast, 'status' | 'cancelledBy'> & { attempts?: number }): boolean =>
  // The API's marker, OR any cancelled row that had been attempted: the host's own per-row cancel loses the marker, but not the attempt count.
  // (Never a duplicate the migration cancelled: that row was not what went out, its keeper was.)
  row.status === 'cancelled'
  && row.cancelledBy !== 'migration:018'
  && (row.cancelledBy === IN_DOUBT_CANCELLED_BY || (row.attempts ?? 0) > 0);

/** What the server's `excluded[].reason` (else its `status`) means to the host. */
export function excludedReasonText(entry: Pick<ExcludedPlanStep, 'status' | 'reason'>): string {
  switch (entry.reason ?? entry.status) {
    case 'sent': return 'already sent';
    case 'sending': return 'being sent right now';
    case 'in_doubt': return 'may have been sent — check before re-sending';
    case 'in_doubt_retrying': return "may have been sent. It will be retried automatically with the same wording — don't re-send it";
    case 'cancelled': return 'was cancelled';
    case 'failed': return 'failed earlier';
    case 'skipped': return 'was skipped';
    default: return entry.reason ?? entry.status;
  }
}

/** "Not recreated: Day before (already sent), Morning of (may have been sent — check before re-sending). If guests didn't get it, send it again from Broadcast.", or '' when nothing was excluded. */
export function describeExcluded(excluded: ReadonlyArray<Pick<ExcludedPlanStep, 'sequenceStep' | 'status' | 'reason'>> | null | undefined): string {
  if (!excluded || excluded.length === 0) return '';
  // The re-send how-to is only for a reminder THIS call cancelled (`in_doubt`); one still live (`in_doubt_retrying`) will be retried by itself, so re-sending would duplicate it.
  const hint = excluded.some((entry) => (entry.reason ?? entry.status) === 'in_doubt') ? ` ${RESEND_HINT}` : '';
  return `Not recreated: ${excluded.map((entry) => `${stepDisplayName(entry.sequenceStep)} (${excludedReasonText(entry)})`).join(', ')}.${hint}`;
}

/** The `excluded` list a 409 `nothing_to_reschedule` carries. */
function excludedOf(error: unknown): ExcludedPlanStep[] {
  const excluded = (error as { response?: { data?: { excluded?: unknown } } })?.response?.data?.excluded;
  return Array.isArray(excluded) ? (excluded as ExcludedPlanStep[]) : [];
}

// ---- confirm text ----

export function audienceKindPhrase(kind: string | null | undefined): string {
  const label = audienceKindLabel(kind);
  return label ? `${label} (decided when it sends)` : 'All guests';
}

// ---- the scheduled list ----

export type ScheduledListItem =
  | { kind: 'row'; row: GroupScheduledBroadcast }
  | { kind: 'plan'; sequenceId: string; rows: GroupScheduledBroadcast[] };

/**
 * Fold rows that share a `sequenceId` into one plan entry, placed where the
 * plan's FIRST row sat in the (already sorted) input; its rows are soonest
 * first. Rows without a sequence — every row from an API that predates #394 —
 * pass through untouched.
 */
export function groupScheduledBySequence(sorted: readonly GroupScheduledBroadcast[]): ScheduledListItem[] {
  const bySequence = new Map<string, GroupScheduledBroadcast[]>();
  for (const row of sorted) {
    if (!row.sequenceId) continue;
    const bucket = bySequence.get(row.sequenceId) ?? [];
    bucket.push(row);
    bySequence.set(row.sequenceId, bucket);
  }
  const emitted = new Set<string>();
  const items: ScheduledListItem[] = [];
  for (const row of sorted) {
    if (!row.sequenceId) {
      items.push({ kind: 'row', row });
    } else if (!emitted.has(row.sequenceId)) {
      emitted.add(row.sequenceId);
      const rows = [...(bySequence.get(row.sequenceId) ?? [])].sort((a, b) => Date.parse(a.sendAt) - Date.parse(b.sendAt));
      items.push({ kind: 'plan', sequenceId: row.sequenceId, rows });
    }
  }
  return items;
}

export interface PlanCounts {
  pending: number;
  sending: number;
  sent: number;
  skipped: number;
  failed: number;
  cancelled: number;
}

export function planCounts(rows: readonly Pick<GroupScheduledBroadcast, 'status'>[]): PlanCounts {
  const counts: PlanCounts = { pending: 0, sending: 0, sent: 0, skipped: 0, failed: 0, cancelled: 0 };
  for (const row of rows) {
    if (row.status in counts) counts[row.status as keyof PlanCounts] += 1;
  }
  return counts;
}

/** "3 reminders cancelled · 1 already ended · 0 being sent right now" */
export function describeSequenceCancel(result: Pick<ScheduledSequenceCancelResult, 'cancelled' | 'alreadyTerminal' | 'inFlight'>): string {
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const parts = [`${plural(result.cancelled, 'reminder', 'reminders')} cancelled`];
  if (result.alreadyTerminal > 0) parts.push(`${result.alreadyTerminal} already finished`);
  if (result.inFlight > 0) parts.push(`${result.inFlight} being sent right now (too late to cancel)`);
  return parts.join(' · ');
}

// ---- date-change guard ----

export interface PlanDrift {
  drifted: boolean;
  /** The trip date this plan was built from (`YYYY-MM-DD`), as the API stored it on the plan's rows; null when it has none. */
  anchor: string | null;
  /** Whole days from the anchor to the trip's CURRENT start date (positive = the trip moved later). */
  shiftDays: number;
}

const localDay = (iso: string, timeZone: string): string | null => {
  const wall = isoToWallClock(iso, timeZone);
  return wall ? wall.slice(0, 10) : null;
};

/**
 * The trip date a plan was built from: the `sequenceAnchorDate` the API stored on its rows (the most common one, should they ever
 * disagree). A plan WITHOUT one (created before the anchor existed, or by an older Studio) has NO anchor and therefore NO
 * date-change detection: guessing the date from the reminder copy or the send times misjudged a step the host had moved on
 * purpose, and a false "trip dates changed" that offers to rewrite reminders is worse than saying nothing.
 */
export function planAnchorDate(rows: readonly GroupScheduledBroadcast[]): string | null {
  const votes = new Map<string, number>();
  for (const row of rows) {
    const anchor = normalizeTripDate(row.sequenceAnchorDate);
    if (anchor) votes.set(anchor, (votes.get(anchor) ?? 0) + 1);
  }
  let best: string | null = null;
  votes.forEach((count, anchor) => { if (best === null || count > (votes.get(best) ?? 0)) best = anchor; });
  return best;
}

/**
 * Has the trip moved since this plan was made? Its stored anchor date differs from the CURRENT `dateStart` and it still has a
 * pending step. A step the host moved to another day, with the trip date unchanged, is NOT drift.
 */
export function detectPlanDateDrift(
  rows: readonly GroupScheduledBroadcast[],
  dateStart: string | null | undefined,
  timeZone: string,
): PlanDrift {
  void timeZone;
  const start = normalizeTripDate(dateStart);
  const anchor = planAnchorDate(rows);
  if (!start || !anchor || !rows.some((row) => row.status === 'pending')) return { drifted: false, anchor, shiftDays: 0 };
  return { drifted: anchor !== start, anchor, shiftDays: daysBetweenYmd(anchor, start) };
}

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Replace every mention of the old trip date label in some copy. The match is bounded: "Sat Oct 1" must not eat the front of
 * "Sat Oct 15" (nor sit inside a longer word). A copy that never named it is returned unchanged.
 */
export function retargetTripDate(text: string | null | undefined, oldLabel: string, newLabel: string): string {
  const value = text ?? '';
  if (oldLabel === newLabel) return value;
  return value.replace(new RegExp(`(?<!\\w)${escapeRegExp(oldLabel)}(?!\\d)`, 'g'), () => newLabel);
}

export interface ReschedulePlan {
  requests: PlanRequest[];
  /** Pending rows that will NOT be recreated, and why (the new time is in the past, or the wording no longer fits once the date is in it). */
  dropped: Array<{ row: GroupScheduledBroadcast; reason: string }>;
  /**
   * Pending rows that were already ATTEMPTED (`attempts > 0`: a send went out, or may have, and the row went back to waiting for a retry). Their outcome is
   * unknown, so the API cancels them and does NOT recreate them (a recreated copy would reach every guest twice); the host is told to check.
   */
  inDoubt: GroupScheduledBroadcast[];
  /** e.g. "Fri Oct 16 → Sat Oct 17"; null when no copy mentioned a date to update. */
  dateChange: string | null;
  /** The trip start date the new plan is built from (sent as its anchor). */
  anchor: string | null;
}

/**
 * What "Reschedule plan" would create: every pending (or sending) step (never a sent, cancelled, failed or skipped one), same copy, SHIFTED by the number of days the trip moved (its stored
 * anchor date -> the current `dateStart`), keeping each step's own offset and time of day in the trip's zone — so a step the
 * host had put on its own day keeps that offset. The anchor date the copy names is rewritten to the new date. A step whose new
 * time is already past, or whose wording no longer fits (subject / text length) once the new date is in it, is dropped and
 * reported, never created invalid or silently lost. A step that would land AFTER the new trip start day is flagged
 * (`lateAfterStart`) for the confirm to hold back. All under `newSequenceId`, sent in ONE atomic call. A plan with no stored anchor
 * cannot be rescheduled from a date change and creates nothing.
 */
export function planReschedule(input: {
  rows: readonly GroupScheduledBroadcast[];
  dateStart: string;
  timeZone: string;
  newSequenceId: string;
  nowMs?: number;
}): ReschedulePlan {
  const nowMs = input.nowMs ?? Date.now();
  const requests: PlanRequest[] = [];
  const dropped: ReschedulePlan['dropped'] = [];
  const anchor = planAnchorDate(input.rows);
  const inDoubt: GroupScheduledBroadcast[] = [];
  if (!anchor) return { requests, dropped, inDoubt, dateChange: null, anchor: null };
  const shift = daysBetweenYmd(anchor, input.dateStart);
  const oldLabel = tripDateLabel(anchor);
  const newLabel = tripDateLabel(input.dateStart);
  let dateChange: string | null = null;
  // Each step keeps its label (a preset key, or one an older Studio minted); a repeat gets a numbered label so the
  // plan never carries two steps with one identity.
  const usedLabels = new Set<string>();
  const labelFor = (row: GroupScheduledBroadcast): string => {
    const wanted = row.sequenceStep ?? 'custom';
    let label = wanted;
    for (let n = 2; usedLabels.has(label); n += 1) label = `${wanted}_${n}`;
    usedLabels.add(label);
    return label;
  };

  for (const row of input.rows) {
    // Only a step that can still go out is recreated: pending, or 'sending' (included so the API can decide and REPORT it). A fresh lease is
    // genuinely being sent (left alone, its step excluded); an EXPIRED one is IN DOUBT (the worker died mid-send, so it may have gone out): the
    // API cancels that row and does not recreate it (excluded, reason `in_doubt`). A row that is sent, cancelled, failed or skipped has ended
    // and must never be brought back by Reschedule (the API refuses it too).
    if (row.status !== 'pending' && row.status !== 'sending') continue;
    // A pending row that was already attempted is IN DOUBT (see ReschedulePlan.inDoubt): it is still sent to the API, which cancels it and does NOT
    // recreate it (and reports it), and the preview says so. (Sending it keeps the API the one decider, and lets Reschedule cancel it even
    // when it is the only step left.)
    const doubtful = row.status === 'pending' && (row.attempts ?? 0) > 0;
    if (doubtful) inDoubt.push(row);
    const day = localDay(row.sendAt, input.timeZone);
    let sendAt: string | null = row.sendAt;
    let newDay = day;
    if (day) {
      const clock = isoToWallClock(row.sendAt, input.timeZone).slice(11);
      newDay = addDaysYmd(day, shift);
      sendAt = wallClockToIso(`${newDay}T${clock}`, input.timeZone);
    }
    const before = `${row.subject}\n${row.body}\n${row.smsBody ?? ''}`;
    const subject = retargetTripDate(row.subject, oldLabel, newLabel);
    const body = retargetTripDate(row.body, oldLabel, newLabel);
    const smsBody = row.smsBody == null ? null : retargetTripDate(row.smsBody, oldLabel, newLabel);
    if (oldLabel !== newLabel && before.includes(oldLabel)) dateChange = `${oldLabel} → ${newLabel}`;

    if (!sendAt || !sendAtIsInFuture(sendAt, nowMs)) {
      if (!doubtful) dropped.push({ row, reason: 'its new time is already in the past' });
      continue;
    }
    // The new date can be longer than the old one: the API refuses over-length text, so re-check rather than fail the whole call.
    if (subject.length > SUBJECT_MAX || body.length > BODY_MAX || (smsBody !== null && smsBody.length > SMS_MAX)) {
      if (!doubtful) dropped.push({ row, reason: 'its wording is over the length limit once the new date is in it' });
      continue;
    }
    const stepLabel = labelFor(row);
    const preset = row.sequenceStep ? PRESET_BY_KEY.get(row.sequenceStep) : undefined;
    requests.push({
      step: stepLabel,
      label: preset?.label ?? row.subject,
      audienceKind: row.audienceKind ?? null,
      sendAt,
      lateAfterStart: newDay !== null && newDay > input.dateStart,
      inDoubt: doubtful,
      request: {
        subject,
        body,
        channels: row.channels,
        smsBody,
        personalizeInviteLinks: row.personalizeInviteLinks,
        // Same rule as a fresh plan: the kind ALONE. A row with no kind (it cannot come from a plan Studio built)
        // keeps whatever audience it had.
        ...(row.audienceKind
          ? { audienceKind: row.audienceKind }
          : { segment: row.segment ?? null, ...(row.inviteeIds ? { inviteeIds: row.inviteeIds } : {}) }),
        sendAt,
        sequenceId: input.newSequenceId,
        sequenceStep: stepLabel,
        sequenceTotalSteps: 0, // set below, once the dropped steps are known
      },
    });
  }
  for (const entry of requests) entry.request.sequenceTotalSteps = requests.length;
  requests.sort((a, b) => Date.parse(a.sendAt) - Date.parse(b.sendAt));
  return { requests, dropped, inDoubt, dateChange, anchor: input.dateStart };
}

/**
 * Has anything happened to a plan since `before` (its rows as this page last saw them)? Used before telling the host "Nothing was changed": another
 * tab's Reschedule, a sweep that sent a step, or a cancel all change what is on the server, and a refused call of THIS tab says nothing of them.
 * `after` is a fresh read of the whole scheduled list; the plan changed when any of its rows has a different status (or is gone) or a plan already
 * exists under the replacement id.
 */
export function planChangedSince(
  before: readonly Pick<GroupScheduledBroadcast, 'id' | 'status'>[],
  after: readonly Pick<GroupScheduledBroadcast, 'id' | 'status' | 'sequenceId'>[],
  newSequenceId: string,
): boolean {
  if (after.some((row) => row.sequenceId === newSequenceId)) return true;
  const statusNow = new Map(after.map((row) => [row.id, row.status]));
  return before.some((row) => statusNow.get(row.id) !== row.status);
}

/** How long a sweep worker's lease lasts before the API treats its `sending` row as abandoned (mirrors the API's `StaleClaimTimeout`). */
export const STALE_LEASE_MS = 15 * 60_000;

/**
 * What a Reschedule that already happened left out, worked out from the list (for the fallback when its answer was lost to a timeout, so the host
 * still sees "may have been sent — check before re-sending"). The same rule as the API's repeat, per step missing from the new plan:
 *  - in doubt, whether or not the request named it: a cancelled row that carries the in-doubt marker (`cancelledBy`) is `in_doubt`; a row still LIVE —
 *    pending and attempted (`attempts > 0`), or `sending` with an expired lease (`lockedAt`, which the API now exposes) — is `in_doubt_retrying`:
 *    the sweep retries it by itself with the same wording.
 *  - for a requested step only: `sending` as such, sent / failed / skipped as such, else cancelled (in doubt if a cancelled row had been attempted).
 * A live `sending` row with a fresh (or unknown) lease is reported `sending`; the API's own answer is authoritative whenever it arrives.
 */
export function deriveExcludedSteps(
  oldRows: readonly GroupScheduledBroadcast[],
  newRows: readonly Pick<GroupScheduledBroadcast, 'sequenceStep'>[],
  requestedSteps: readonly string[],
  nowMs: number = Date.now(),
): ExcludedPlanStep[] {
  const present = new Set(newRows.map((row) => row.sequenceStep));
  const requested = new Set(requestedSteps);
  const excluded: ExcludedPlanStep[] = [];
  const labels = [...new Set(oldRows.map((row) => row.sequenceStep).filter((label): label is string => Boolean(label)))];
  const leaseExpired = (row: GroupScheduledBroadcast) => {
    const lockedAt = row.lockedAt ? Date.parse(row.lockedAt) : Number.NaN;
    return row.status === 'sending' && Number.isFinite(lockedAt) && nowMs - lockedAt > STALE_LEASE_MS;
  };
  for (const label of labels) {
    if (present.has(label)) continue;
    const mine = oldRows.filter((row) => row.sequenceStep === label);
    const live = mine.find((row) => row.status !== 'cancelled');
    const attempted = mine.some((row) => (row.attempts ?? 0) > 0 || row.cancelledBy === IN_DOUBT_CANCELLED_BY);
    if (live && ((live.status === 'pending' && (live.attempts ?? 0) > 0) || leaseExpired(live))) {
      // Still live: the API retries it by itself (reported `in_doubt_retrying`, like the API's repeat), so it must not be offered for a hand re-send.
      excluded.push({ sequenceStep: label, status: live.status, reason: 'in_doubt_retrying' });
    } else if (!live && mine.some((row) => row.cancelledBy === IN_DOUBT_CANCELLED_BY)) {
      excluded.push({ sequenceStep: label, status: 'cancelled', reason: 'in_doubt' });
    } else if (!requested.has(label)) {
      continue;
    } else if (!live) {
      excluded.push({ sequenceStep: label, status: 'cancelled', reason: attempted ? 'in_doubt' : 'cancelled' });
    } else if (live.status === 'sending') {
      excluded.push({ sequenceStep: label, status: 'sending', reason: 'sending' });
    } else if (live.status !== 'pending') {
      excluded.push({ sequenceStep: label, status: live.status, reason: live.status });
    }
  }
  return excluded;
}
